import React, { useState } from 'react';
import { usePricing } from '../../contexts/PricingContext';
import { useTheme } from '../../contexts/ThemeContext';
import { PricingTier } from '../../types';
import { Combobox, Transition } from '@headlessui/react';
import { countries } from '../../utils/countries';
import {
    GlobeAltIcon,
    CalendarIcon,
    UserGroupIcon,
    SparklesIcon,
    CheckIcon,
    ChevronUpDownIcon
} from '@heroicons/react/24/outline';

const PricingFilter: React.FC = () => {
    const {
        countryCode,
        tier,
        travelDates,
        pax,
        setCountry,
        setTier,
        setDates,
        setPax,
        isFilterOpen,
        setIsFilterOpen
    } = usePricing();
    const { actualTheme } = useTheme();

    const [query, setQuery] = useState('');

    const filteredCountries =
        query === ''
            ? countries
            : countries.filter((country) =>
                country.name
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .includes(query.toLowerCase().replace(/\s+/g, ''))
            );

    const selectedCountry = countries.find(c => c.code === countryCode) || countries[0];

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 relative z-20">
            <div className={`rounded-3xl shadow-premium border backdrop-blur-xl transition-all duration-300 overflow-hidden ${actualTheme === 'dark'
                ? 'bg-slate-900/95 border-slate-800/50'
                : 'bg-white/95 border-neutral-200/50'
                }`}>

                {/* Header / Toggle Section */}
                <div
                    id="pricing-filter-trigger"
                    className="p-4 md:p-6 flex flex-col md:flex-row items-center justify-between cursor-pointer group"
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-2xl ${actualTheme === 'dark' ? 'bg-primary-900/50 text-primary-400' : 'bg-primary-50 text-primary-600'}`}>
                            <SparklesIcon className="w-6 h-6 animate-pulse" />
                        </div>
                        <div>
                            <h3 className={`text-lg md:text-xl font-bold ${actualTheme === 'dark' ? 'text-neutral-100' : 'text-neutral-900'}`}>
                                Customize Your Safari Experience
                            </h3>
                            <p className={`text-xs md:text-sm font-medium ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'}`}>
                                {isFilterOpen ? 'Hide filters' : 'Tap to personalize pricing & suggestions'}
                            </p>
                        </div>
                    </div>

                    <button
                        className={`mt-4 md:mt-0 px-6 py-2 rounded-full text-sm font-bold transition-all ${isFilterOpen
                            ? actualTheme === 'dark' ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                            : 'bg-blue-500 text-white shadow-lg shadow-blue-500/30 hover:bg-blue-600 hover:scale-105'
                            }`}
                    >
                        {isFilterOpen ? 'Close' : 'Start Planning'}
                    </button>
                </div>

                {/* Collapsible Content */}
                <Transition
                    show={isFilterOpen}
                    enter="transition ease-out duration-300"
                    enterFrom="opacity-0 max-h-0"
                    enterTo="opacity-100 max-h-[800px]"
                    leave="transition ease-in duration-200"
                    leaveFrom="opacity-100 max-h-[800px]"
                    leaveTo="opacity-0 max-h-0"
                >
                    <div className="p-6 md:p-8 pt-0 border-t border-dashed border-gray-200 dark:border-slate-800/50">
                        {/* Mobile: Stack vertically, Desktop: Grid */}
                        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-12 lg:gap-8 pt-6">

                            {/* Nationality Section (Searchable Dropdown) */}
                            <div className="lg:col-span-3">
                                <label className="flex items-center gap-2 text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest mb-3">
                                    <GlobeAltIcon className="h-4 w-4" />
                                    Origin & Nationality
                                </label>

                                <Combobox value={selectedCountry} onChange={(c: any) => c && setCountry(c.code)}>
                                    <div className="relative mt-1">
                                        <div className="relative w-full cursor-default rounded-full overflow-hidden text-left shadow-md hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-opacity-75 transition-all sm:text-sm">
                                            <Combobox.Input
                                                className={`w-full border-2 py-3.5 pl-4 pr-10 text-sm font-semibold leading-5 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all ${actualTheme === 'dark'
                                                    ? 'bg-slate-800 text-neutral-100 placeholder-slate-400 border-slate-700 hover:border-slate-600'
                                                    : 'bg-white text-neutral-900 placeholder-neutral-400 border-neutral-200 hover:border-neutral-300'
                                                    }`}
                                                displayValue={(country: any) => country?.name}
                                                onChange={(event) => setQuery(event.target.value)}
                                                placeholder="Search country..."
                                            />
                                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                <ChevronUpDownIcon
                                                    className={`h-5 w-5 transition-colors ${actualTheme === 'dark' ? 'text-slate-400 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
                                                    aria-hidden="true"
                                                />
                                            </Combobox.Button>
                                        </div>
                                        <Transition
                                            as={React.Fragment}
                                            leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0"
                                        >
                                            <Combobox.Options className={`absolute mt-2 max-h-60 w-full overflow-auto rounded-2xl py-2 text-base shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50 ${actualTheme === 'dark'
                                                ? 'bg-slate-800 border border-slate-700'
                                                : 'bg-white border border-neutral-200'
                                                }`}>
                                                {filteredCountries.length === 0 && query !== '' ? (
                                                    <div className={`relative cursor-default select-none py-3 px-4 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-700'}`}>
                                                        Nothing found.
                                                    </div>
                                                ) : (
                                                    filteredCountries.map((country) => (
                                                        <Combobox.Option
                                                            key={country.code}
                                                            className={({ active }) =>
                                                                `relative cursor-pointer select-none py-3 pl-10 pr-4 mx-2 rounded-xl transition-colors ${active
                                                                    ? actualTheme === 'dark'
                                                                        ? 'bg-slate-700 text-white'
                                                                        : 'bg-primary-50 text-primary-900'
                                                                    : actualTheme === 'dark'
                                                                        ? 'text-neutral-100'
                                                                        : 'text-gray-900'
                                                                }`
                                                            }
                                                            value={country}
                                                        >
                                                            {({ selected, active }) => (
                                                                <>
                                                                    <span className={`block truncate font-semibold ${selected ? 'font-bold' : 'font-medium'}`}>
                                                                        {country.flag} {country.name}
                                                                    </span>
                                                                    {selected ? (
                                                                        <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active
                                                                            ? actualTheme === 'dark' ? 'text-white' : 'text-primary-600'
                                                                            : actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                                                                            }`}>
                                                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                                        </span>
                                                                    ) : null}
                                                                </>
                                                            )}
                                                        </Combobox.Option>
                                                    ))
                                                )}
                                            </Combobox.Options>
                                        </Transition>
                                    </div>
                                </Combobox>

                                <p className="mt-2 text-[10px] text-neutral-400 dark:text-slate-500 text-center font-medium italic">
                                    *Local rates apply for Kenyan Residents
                                </p>
                            </div>

                            {/* Tier Logic Section */}
                            <div className="lg:col-span-4">
                                <label className="flex items-center gap-2 text-xs font-bold text-accent-600 dark:text-accent-400 uppercase tracking-widest mb-3">
                                    <SparklesIcon className="h-4 w-4" />
                                    Experience Tier
                                </label>
                                <div className="flex gap-2">
                                    {Object.values(PricingTier).map((t) => (
                                        <label
                                            key={t}
                                            className={`flex-1 min-w-[100px] flex items-center justify-center gap-1.5 px-3 py-2 rounded-full cursor-pointer transition-all border-2 ${tier === t
                                                ? actualTheme === 'dark'
                                                    ? 'bg-slate-800 border-primary-500 shadow-sm'
                                                    : 'bg-white border-primary-500 shadow-sm'
                                                : actualTheme === 'dark'
                                                    ? 'bg-slate-800/50 border-transparent hover:border-slate-700'
                                                    : 'bg-neutral-50 border-transparent hover:border-neutral-200'
                                                }`}
                                        >
                                            <input
                                                type="radio"
                                                name="tier-filter"
                                                value={t}
                                                checked={tier === t}
                                                onChange={() => setTier(t)}
                                                className="w-3.5 h-3.5 text-primary-600 focus:ring-primary-500 border-gray-300"
                                            />
                                            <span className={`text-[10px] font-bold ${tier === t
                                                ? actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-700'
                                                : actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                                                }`}>
                                                {t}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                <p className="mt-2 text-[10px] text-neutral-400 dark:text-slate-500 text-center font-medium">
                                    {tier === 'LUXURY' && '✨ 5-Star Lodges & Senior Guides'}
                                    {tier === 'STANDARD' && '🐘 Premium Camps & Guides'}
                                    {tier === 'BUDGET' && '⛺ Adventure Tents'}
                                </p>
                            </div>

                            {/* Seasons & Dates Section */}
                            <div className="lg:col-span-2">
                                <label className="flex items-center gap-2 text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-3">
                                    <CalendarIcon className="h-4 w-4" />
                                    Travel Dates
                                </label>
                                <div className="relative group">
                                    <input
                                        type="date"
                                        value={travelDates?.startDate || ''}
                                        onChange={(e) => setDates(e.target.value, travelDates?.endDate || '')}
                                        className={`w-full pl-4 pr-10 py-4 border rounded-2xl text-sm font-semibold appearance-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all cursor-pointer shadow-sm ${actualTheme === 'dark'
                                            ? 'bg-slate-800 border-slate-700 text-neutral-100 hover:border-primary-500'
                                            : 'bg-white border-neutral-200 text-neutral-800 hover:border-primary-400'
                                            }`}
                                    />
                                    <div className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none ${actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-400'}`}>
                                        <CalendarIcon className="h-4 w-4" />
                                    </div>
                                </div>
                            </div>

                            {/* PAX Section */}
                            <div className="lg:col-span-3">
                                <label className="flex items-center gap-2 text-xs font-bold text-orange-600 dark:text-orange-400 uppercase tracking-widest mb-3">
                                    <UserGroupIcon className="h-4 w-4" />
                                    Group Composition
                                </label>
                                <div className={`flex items-center gap-4 border rounded-2xl px-4 h-[58px] shadow-sm ${actualTheme === 'dark'
                                    ? 'bg-slate-800 border-slate-700'
                                    : 'bg-white border-neutral-200'
                                    }`}>
                                    <div className="flex-1 flex flex-col items-center">
                                        <span className={`text-[10px] font-bold uppercase ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-400'}`}>Adults</span>
                                        <input
                                            type="number"
                                            min="1"
                                            className={`w-full bg-transparent border-none text-center text-sm font-bold focus:ring-0 p-0 ${actualTheme === 'dark' ? 'text-neutral-100' : 'text-neutral-800'}`}
                                            value={pax.adults}
                                            onChange={(e) => setPax(Math.max(1, parseInt(e.target.value) || 1), pax.children)}
                                        />
                                    </div>
                                    <div className={`w-px h-8 ${actualTheme === 'dark' ? 'bg-slate-700' : 'bg-neutral-200'}`}></div>
                                    <div className="flex-1 flex flex-col items-center">
                                        <span className={`text-[10px] font-bold uppercase ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-400'}`}>Children</span>
                                        <input
                                            type="number"
                                            min="0"
                                            className={`w-full bg-transparent border-none text-center text-sm font-bold focus:ring-0 p-0 ${actualTheme === 'dark' ? 'text-neutral-100' : 'text-neutral-800'}`}
                                            value={pax.children}
                                            onChange={(e) => setPax(pax.adults, Math.max(0, parseInt(e.target.value) || 0))}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
    );
};

export default PricingFilter;
